import AlertArea from "../ui/AlertArea.js";
import UserArea from "../ui/UserArea.js";

import Error from "../utils/Error.js";
import global from "../global.js";

const log = global.log.getLogger("f_mtac.server.registrar");
log.setLevel(log.levels.WARN);

let RegistrarUser = function (server) {
    var data_store = null;

    Object.defineProperty(this, "user", {
        set: () => {},
        get: () => global.configuration.user
    });

    var mtac_user_register = ({passphrase, name, capabilities, level, org_id}) => {
        log.debug("mtac_user_register IN ", {passphrase, name, capabilities, level, org_id});

        const access_control = get_user_rights(level);
        return server
            .put(
                "/registrar",
                // access_control = user level (expert, standard, observer) shall have been renamed "level" but pb for export/import
                {passphrase, name, capabilities, access_control, org_id},
                {
                    401: (error) => {
                        throw new Error.build.access({
                            message: `user register was not authorized by the server (${error.message})`,
                            data: {error}
                        });
                    },
                    403: (error) => {
                        throw new Error.build.perm({
                            message: `user register was not authorized by the server (${error.message})`,
                            data: {error}
                        });
                    }
                }
            )
            .then((response) => response.json());
    };

    var get_user_rights = (level) => {
        var profile = global.configuration.profiles[level];
        return profile.rights;
    };

    var mtac_user_update = ({name, capabilities, level}) => {
        log.debug("Registrar.mtac_user_update", {name, capabilities, level});
        const access_control = get_user_rights(level);
        const org_id = global.configuration.commissioning.organization;
        return server
            .post(
                "/user",
                {name, capabilities, access_control, org_id},
                {
                    401: (error) => {
                        throw new Error.build.access({
                            message: `user update was not authorized by the server (${error.message})`,
                            data: {error}
                        });
                    },
                    404: (error) => {
                        throw new Error.build.noent({
                            message: `the user was not found on the server (${error.message})`,
                            data: {error}
                        });
                    }
                }
            )
            .then((response) => response.json());
    };

    this.init = async () => {
        log.debug("Registrar.init IN");
        let date_now = Date.now();
        data_store = await global.configuration.add_store("user", {
            name: "Anonymous",
            capabilities: 0,
            level: 0,
            created: date_now,
            updated: date_now,
            synced: 0
        });
        data_store.on("create", ({store_data}) => {
            log.debug("Registrar.user data created", store_data);
        });
        data_store.on("update", ({modifications}) => {
            log.info("Registrar.user modifications", modifications);
        });
    };

    this.reset_token = async () => {
        log.debug("Registrar.reset_token IN");
        this.user.token = undefined;
        await this.commit();
        await this.push();
    };

    this.commit = async () => {
        this.user.updated = Date.now();
        await data_store.save();
    };

    this.push = async () => {
        log.debug("Registrar.push IN");
        if (this.user.updated < this.user.synced) {
            return;
        }
        try {
            await mtac_user_update(this.user);
            this.user.synced = Date.now();
            await data_store.save();
        } catch (error) {
            switch (error.status) {
                case Error.status.netdown:
                case Error.status.hostdown:
                    log.warn("Registrar.push failed to update user, network is down, will re-try again later");
                    break;
                case Error.status.access:
                    log.error("Registrar.push failed to update user, access refused, try to re-register");
                    await this.register_retry(this.user);
                    break;
            }
        }
    };

    var registration = null;
    var register_retry = async (user_data) => {
        log.debug("Registrar.register_retry IN async", {user_data});
        var register_error = null;
        do {
            register_error = null;
            try {
                await this.register(user_data);
            } catch (error) {
                log.debug("Registrar.register_retry Error:", error);
                switch (error.status) {
                    case Error.status.netdown:
                    case Error.status.hostdown:
                        await AlertArea.interact({
                            type: "danger",
                            tip1: global.internationalization.text("user.Registration-error-server-unreachable")
                        });
                        register_error = error;
                        break;
                    case Error.status.access:
                    case Error.status.invalid:
                        await AlertArea.interact({
                            type: "danger",
                            tip1: global.internationalization.text("user.Registration-error-wrong-password")
                        });
                        register_error = null;
                        break;
                    default:
                        await AlertArea.interact({
                            type: "danger",
                            tip1: global.internationalization.text("user.Registration-error", {error: error.message})
                        });
                        register_error = null;
                        break;
                }
            }
        } while (register_error !== null);
    };

    this.register_retry = (user_data) => {
        log.debug("Registrar.register_retry IN", {user_data});
        if (registration !== null) {
            return registration;
        }
        registration = register_retry(user_data).then((result) => {
            registration = null;
            return result;
        });
        return registration;
    };

    const sleep = (ms) => {
        return new Promise((resolve) => setTimeout(resolve, ms));
    };

    /**
     * @brief PUT mtac/registrar to the server
     * @param {*} name in, optional
     * @param {*} capabilities in, optional
     * @param {*} level in, optional
     * @param {*} passphrase in, optional
     * @throws Forward throws from mtac_user_register(), containing error from server
     */

    this.force_register_agnet = async () => {
        log.debug("Registrar.force_register_agnet IN");
        // Mode AGNET registration: set token to undefined so that f_agnet landing do the registration
        // otherwise we would have an infinite loop between "/" and "/mtac".
        this.user.token = undefined;
        await this.commit();
        // This ugly sleep is needed otherwise the token is not undefined on "/" despite the await.
        await sleep(100);
        log.debug("Registrar.force_register_agnet: move to /");
        window.location.replace("/");
    };

    this.register = async ({name, capabilities, level, passphrase, org_id}) => {
        log.debug("Registrar.register IN", {name, capabilities, level, passphrase});
        if (passphrase === undefined) {
            if (window.location.pathname !== "/") {
                if (this.user.agnet !== undefined) {
                    // Mode AGNET registration: set token to undefined so that f_agnet landing do the registration
                    // otherwise we would have an infinite loop between "/" and "/mtac".
                    this.user.token = undefined;
                    await this.commit();
                    // This ugly sleep is needed otherwise the token is not undefined on "/" despite the await.
                    await sleep(100);
                }
                log.debug("Registrar.register: move to /");
                window.location.replace("/");
            }
            throw new Error.build.invalid({message: "no passphrase provided"});
        }
        if (passphrase === null) {
            log.error("no passphrase provided");
            return;
        }
        if (level !== undefined) {
            this.user.level = level;
        }
        if (name !== undefined) {
            this.user.name = name;
        }
        if (capabilities !== undefined) {
            this.user.capabilities = capabilities;
        }
        if (org_id !== undefined) {
            this.user.org_id = org_id;
        }
        ({name, capabilities, level, org_id} = this.user);

        var {login, token} = (await mtac_user_register({passphrase, name, capabilities, level, org_id})).data;

        log.debug("data store user ", {synced: Date.now(), login, token});
        Object.assign(this.user, {synced: Date.now(), login, token});
        await data_store.save();
    };

    this.update = async ({name, capabilities, level}) => {
        log.debug("Registrar.update IN");
        if (level !== undefined) {
            this.user.level = level;
        }
        if (name !== undefined) {
            this.user.name = name;
        }
        if (capabilities !== undefined) {
            this.user.capabilities = capabilities;
        }
        await this.commit();
        await this.push();
    };

    this.set_capability = async (capability) => {
        log.debug("Registrar.set_capability IN");
        this.user.capabilities |= capability;
        await this.commit();
        await this.push();
    };

    this.unset_capability = async (capability) => {
        log.debug("Registrar.unset_capability IN");
        this.user.capabilities &= ~capability;
        await this.commit();
        await this.push();
    };

    global.event_bus.subscribe("network.online", () => {
        log.debug("Registrar.on network.online Registrar:subscribe IN");
        // AGPOC-4769 : for Agnet case
        if (this.user !== undefined) {
            if (this.user.token !== undefined) {
                this.push();
            }
        }
    });
};

global.app.bootstrap.register_module({
    name: "registrar",
    deps: ["app", "configuration", "server"],
    init: async () => {
        var server = global.server;
        var registrar = global.server.registrar;
        if (registrar.user.token === undefined) {
            await registrar.register_retry({});
            return;
        }
        try {
            log.debug("Registrar. register_module");
            await server.network.ping();
        } catch (error) {
            switch (error.status) {
                case Error.status.netdown:
                case Error.status.hostdown:
                    log.warn("application is starting offline");
                    break;
                default:
                    log.error("error during ping");
                    break;
            }
        }
    }
});

var device_registration;

global.app.bootstrap.register_module({
    name: "device_registration",
    deps: ["registrar"],
    init: async () => {
        // assuming user is registered if level != 0
        if (global.server.registrar.user.level !== 0) {
            return;
        }
        device_registration = UserArea.interact();
    }
});

global.app.bootstrap.register_module({
    name: "check_device_registration",
    deps: ["device_registration"],
    init: async () => {
        await device_registration;
    }
});

export default RegistrarUser;
